import axios, { AxiosRequestConfig } from 'axios';
import { baseURL } from '../config';
import ApiHelper from './ApiHelper';
import IResponse from '../interfaces/IResponse';

const BASE_URL = `${baseURL}api/rfid-tags`;

export type IRfidEmployee = {
  _id: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
};

export type IRfidTag = {
  _id: string;
  uid: string;
  name?: string;
  isActive: boolean;
  employee: IRfidEmployee | null;
  assignedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

interface IGetAllRfidTagsResponse extends IResponse {
  tags: IRfidTag[];
}

export type GetAllRfidTagsParams = {
  isActive?: boolean;
  assigned?: boolean;
};

const getAllRfidTags = async (params?: GetAllRfidTagsParams): Promise<IGetAllRfidTagsResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
      params,
    };

    const res = await axios.get(BASE_URL, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Pobrano tagi RFID',
      tags: res.data?.tags || [],
    };
  } catch (error) {
    let message = 'Nie udało się pobrać tagów RFID';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, tags: [] };
  }
};

interface ICreateRfidTagResponse extends IResponse {
  tag: IRfidTag | null;
}

const createRfidTag = async (
  payload: { uid: string; name?: string; isActive?: boolean }
): Promise<ICreateRfidTagResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
    };

    const res = await axios.post(BASE_URL, payload, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Dodano tag RFID',
      tag: res.data?.tag || null,
    };
  } catch (error) {
    let message = 'Nie udało się dodać taga RFID';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, tag: null };
  }
};

interface IUpdateRfidTagResponse extends IResponse {
  tag: IRfidTag | null;
}

const updateRfidTag = async (
  tagId: string,
  payload: { uid?: string; name?: string; isActive?: boolean }
): Promise<IUpdateRfidTagResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
    };

    const res = await axios.put(`${BASE_URL}/${tagId}`, payload, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Zaktualizowano tag RFID',
      tag: res.data?.tag || null,
    };
  } catch (error) {
    let message = 'Nie udało się zaktualizować taga RFID';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, tag: null };
  }
};

interface IAssignEmployeeResponse extends IResponse {
  tag: IRfidTag | null;
}

const assignEmployeeToTag = async (tagId: string, employeeId: string): Promise<IAssignEmployeeResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
    };

    const res = await axios.post(`${BASE_URL}/${tagId}/assign`, { employeeId }, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Przypisano pracownika do taga',
      tag: res.data?.tag || null,
    };
  } catch (error) {
    let message = 'Nie udało się przypisać pracownika do taga';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, tag: null };
  }
};

const rfidTagsController = {
  getAllRfidTags,
  createRfidTag,
  updateRfidTag,
  assignEmployeeToTag,
};

export default rfidTagsController;
